"use client"

import * as React from "react"
import { Menu as MenuPrimitive } from "@base-ui/react/menu"
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from "lucide-react"
import { actionSheet } from "@/design-system/action-sheet.ts"
import { BottomSheetHandle } from "@/primitives/drawer"
import { useBackDismiss } from "@/design-system/use-back-dismiss.ts"
import { useOverlayForm, useOverlayOpen } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"

/*
 * beacon: a menu chooses its form as it opens (`useOverlayForm`) and keeps it while open: anchored
 * to its trigger on the desk, and on `compact` docked as an action sheet (`action-sheet.ts`) over
 * the scrim, which Back closes. A submenu drills in within the sheet: it docks over its menu, which
 * fades behind it, and leads with a Back row. Destructive items come last, as RowActions orders
 * them.
 */
const MenuDockedContext = React.createContext(false)

/** beacon: inside a submenu, how to close it, which its docked Back row does. */
const SubmenuContext = React.createContext<{ close: () => void } | undefined>(undefined)

function DropdownMenu({ actionsRef, ...props }: MenuPrimitive.Root.Props) {
  const ownActions = React.useRef<MenuPrimitive.Root.Actions>(null)
  const actions = actionsRef ?? ownActions
  const { open, docked, onOpenChange } = useOverlayForm(props)
  useBackDismiss(open && docked, () => actions.current?.close())
  return (
    <MenuDockedContext value={docked}>
      <MenuPrimitive.Root
        data-slot="dropdown-menu"
        actionsRef={actions}
        {...props}
        onOpenChange={onOpenChange}
      />
    </MenuDockedContext>
  )
}

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
}

function DropdownMenuContent({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  children,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset">) {
  const docked = React.use(MenuDockedContext)
  const submenu = React.use(SubmenuContext)
  return (
    <MenuPrimitive.Portal>
      {docked && !submenu && <MenuPrimitive.Backdrop className={actionSheet.backdrop} />}
      <MenuPrimitive.Positioner
        className={cn("isolate z-50 outline-none", docked && actionSheet.positioner)}
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          data-docked={docked || undefined}
          data-bottom-sheet={docked || undefined}
          className={cn(
            docked
              ? // beacon: an action sheet, which fades while a submenu docks over it.
                cn(
                  actionSheet.popup,
                  "transition-opacity has-[[data-slot=dropdown-menu-sub-trigger][data-popup-open]]:pointer-events-none has-[[data-slot=dropdown-menu-sub-trigger][data-popup-open]]:opacity-0",
                )
              : // beacon: sized to its content (12 to 20rem), never clipped to the trigger's width,
                // and lifted by the popover shadow, whose hairline replaces the ring. Enters over
                // 150ms.
                "z-50 max-h-(--available-height) w-max max-w-80 min-w-48 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-ui text-popover-foreground shadow-popover duration-150 ease-snappy outline-none data-[side=bottom]:slide-in-from-top-1 data-[side=inline-end]:slide-in-from-left-1 data-[side=inline-start]:slide-in-from-right-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:overflow-hidden data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
            className,
            // beacon: a caller's width gives way to the sheet's.
            docked && "w-full max-w-none min-w-0",
          )}
          {...props}
        >
          {/* beacon: docked, under the handle every bottom sheet carries. */}
          {docked && <BottomSheetHandle className={actionSheet.handle} />}
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & {
  inset?: boolean
}) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      // beacon: a group's label is tertiary meta text.
      className={cn(
        "px-2 py-1.5 text-xs font-medium text-subtle-foreground data-inset:pl-8",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: MenuPrimitive.Item.Props & {
  inset?: boolean
  variant?: "default" | "destructive"
}) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        // beacon: 32px rows (44px on touch) in UI text with 16px icons resting muted and lighting up on focus (an
        // icon with a colour of its own, such as a Status glyph, keeps it), highlighted in the
        // popover's own accent, which reads on a popover in both schemes; a destructive item is red
        // throughout, icons included, on a red-tinted focus fill.
        "group/dropdown-menu-item relative flex h-8 touch:min-h-11 cursor-default items-center gap-2 rounded-md px-2 text-ui outline-hidden select-none focus:bg-popover-accent focus:text-accent-foreground data-inset:pl-8 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='text-'])]:text-muted-foreground focus:[&_svg:not([class*='text-'])]:text-foreground [&_svg:not([class*='size-'])]:size-4 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:[&_svg]:text-destructive!",
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuSub({ actionsRef, ...props }: MenuPrimitive.SubmenuRoot.Props) {
  // beacon: a docked submenu closes on Back, and on its Back row, to the menu it drilled in from.
  const ownActions = React.useRef<MenuPrimitive.Root.Actions>(null)
  const actions = actionsRef ?? ownActions
  const { open, onOpenChange } = useOverlayOpen(props)
  const docked = React.use(MenuDockedContext)
  const close = React.useCallback(() => actions.current?.close(), [actions])
  useBackDismiss(open && docked, close)
  const submenu = React.useMemo(() => ({ close }), [close])
  return (
    <SubmenuContext value={submenu}>
      <MenuPrimitive.SubmenuRoot
        data-slot="dropdown-menu-sub"
        actionsRef={actions}
        {...props}
        onOpenChange={onOpenChange}
      />
    </SubmenuContext>
  )
}

function DropdownMenuSubTrigger({
  className,
  inset,
  chevron = true,
  children,
  openOnHover,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & {
  inset?: boolean
  /** beacon: false for a trigger that is only an icon, such as a row's ⋯, which needs none. */
  chevron?: boolean
}) {
  // beacon: a docked submenu drills in on a press alone. Opened by a hover it would cover the row
  // the pointer was heading for, and the menu under it, which hides while it is open, would hand
  // the pointer back to this row and open it again.
  const docked = React.use(MenuDockedContext)
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      openOnHover={docked ? false : openOnHover}
      className={cn(
        // beacon: as an item (the popover's accent on focus and while open), with a subtle chevron
        // at the end.
        "flex h-8 touch:min-h-11 cursor-default items-center gap-2 rounded-md px-2 text-ui outline-hidden select-none focus:bg-popover-accent focus:text-accent-foreground data-inset:pl-8 data-popup-open:bg-popover-accent data-popup-open:text-accent-foreground data-open:bg-popover-accent data-open:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='text-'])]:text-muted-foreground focus:[&_svg:not([class*='text-'])]:text-foreground [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      {chevron && <ChevronRightIcon className="ml-auto size-3.5 text-subtle-foreground" />}
    </MenuPrimitive.SubmenuTrigger>
  )
}

function DropdownMenuSubContent({
  align = "start",
  alignOffset = -3,
  side = "right",
  sideOffset = 0,
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
  const docked = React.use(MenuDockedContext)
  const submenu = React.use(SubmenuContext)
  return (
    <DropdownMenuContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        // beacon: the same surface as the menu itself.
        "min-w-40",
        className,
      )}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    >
      {docked && submenu && (
        // beacon: docked, it leads with the way back to the menu it covers.
        <>
          <DropdownMenuItem closeOnClick={false} onClick={submenu.close}>
            <ChevronLeftIcon />
            Back
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {children}
    </DropdownMenuContent>
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  inset,
  ...props
}: MenuPrimitive.CheckboxItem.Props & {
  inset?: boolean
}) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        // beacon: an item with its indicator on the left, in a 16px slot: a 14px box that fills
        // with the primary colour and a white check when on. Highlighted as an item.
        "group/dropdown-menu-checkbox-item relative flex h-8 touch:min-h-11 cursor-default items-center gap-2 rounded-md pr-2 pl-8 text-ui outline-hidden select-none focus:bg-popover-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>svg:not([class*='text-'])]:text-muted-foreground focus:[&>svg:not([class*='text-'])]:text-foreground",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span
        className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center rounded-sm border border-subtle-foreground bg-background text-primary-foreground group-data-[checked]/dropdown-menu-checkbox-item:border-primary group-data-[checked]/dropdown-menu-checkbox-item:bg-primary"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator>
          <CheckIcon className="size-3" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return <MenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
}

function DropdownMenuRadioItem({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.RadioItem.Props & {
  inset?: boolean
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(
        // beacon: an item with the chosen one's check on the left, in a 16px slot. Highlighted as an
        // item.
        "relative flex h-8 touch:min-h-11 cursor-default items-center gap-2 rounded-md pr-2 pl-8 text-ui outline-hidden select-none focus:bg-popover-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&>svg:not([class*='text-'])]:text-muted-foreground focus:[&>svg:not([class*='text-'])]:text-foreground",
        className,
      )}
      {...props}
    >
      <span
        className="pointer-events-none absolute left-2 flex size-4 items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator>
          <CheckIcon className="size-3.5 text-foreground" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  )
}

function DropdownMenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      // beacon: the subtle hairline.
      className={cn("-mx-1 my-1 h-px bg-border-subtle", className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        // beacon: styled as a Kbd chip, at the end of the row, and gone on touch, as a Kbd is. On
        // the highlighted row its fill sits within a hair of the highlight, so the ring takes the
        // default hairline to keep its shape.
        "ml-auto inline-flex touch:hidden h-5 min-w-5 items-center justify-center rounded-sm bg-muted px-1 text-2xs font-medium text-muted-foreground tabular-nums ring-1 ring-border-subtle ring-inset group-focus/dropdown-menu-item:ring-border",
        className,
      )}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
